import type { OrganizationDTOV2 } from '@workers-benefit-fund/api'

import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'

import { getLastExplicitLoginAt } from './organizationSwitchSession.utils'

export const LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY =
  'w360-organization-last-valid-location'
export const PENDING_AUTHENTICATED_LOCATION_STORAGE_KEY =
  'w360-pending-authenticated-location'

const PENDING_AUTHENTICATED_LOCATION_MAX_AGE = 24 * 60 * 60 * 1000

type OrganizationContext = Pick<OrganizationDTOV2, 'id' | 'name'>

interface SavedOrganizationLocation {
  organizationId: string
  path: string
  timestamp: number
}

interface PendingAuthenticatedLocation {
  path: string
  timestamp: number
}

const getSafeSameOriginPath = (path: unknown): string | null => {
  if (
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//')
  ) {
    return null
  }

  const url = new URL(path, window.location.origin)
  return url.origin === window.location.origin
    ? `${url.pathname}${url.search}${url.hash}`
    : null
}

const getSafeOrganizationPath = (
  path: unknown,
  organization: OrganizationContext,
): string | null => {
  if (
    !organization.id ||
    !organization.name ||
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//')
  ) {
    return null
  }

  const safePath = getSafeSameOriginPath(path)
  if (!safePath) return null

  const url = new URL(safePath, window.location.origin)
  const organizationPrefix = `/${cleanStringToBeUrlReadable(organization.name)}`
  if (
    url.origin !== window.location.origin ||
    (url.pathname !== organizationPrefix &&
      !url.pathname.startsWith(`${organizationPrefix}/`))
  ) {
    return null
  }

  return `${url.pathname}${url.search}${url.hash}`
}

export const rememberPendingAuthenticatedLocation = (path: string) => {
  try {
    const safePath = getSafeSameOriginPath(path)
    if (!safePath) return

    sessionStorage.setItem(
      PENDING_AUTHENTICATED_LOCATION_STORAGE_KEY,
      JSON.stringify({
        path: safePath,
        timestamp: Date.now(),
      } satisfies PendingAuthenticatedLocation),
    )
  } catch {
    // Login can still continue to the default landing page without storage.
  }
}

export const getPendingAuthenticatedLocation = (
  organization: OrganizationContext,
): string | null => {
  try {
    const serialized = sessionStorage.getItem(
      PENDING_AUTHENTICATED_LOCATION_STORAGE_KEY,
    )
    if (!serialized) return null

    const location = JSON.parse(serialized) as PendingAuthenticatedLocation
    if (
      !Number.isFinite(location.timestamp) ||
      Date.now() - location.timestamp > PENDING_AUTHENTICATED_LOCATION_MAX_AGE
    ) {
      clearPendingAuthenticatedLocation()
      return null
    }

    return getSafeOrganizationPath(location.path, organization)
  } catch {
    clearPendingAuthenticatedLocation()
    return null
  }
}

export const clearPendingAuthenticatedLocation = () => {
  try {
    sessionStorage.removeItem(PENDING_AUTHENTICATED_LOCATION_STORAGE_KEY)
  } catch {
    // Storage cleanup must not interrupt navigation.
  }
}

export const rememberLastValidOrganizationLocation = (
  organization: OrganizationContext,
  path: string,
) => {
  try {
    const safePath = getSafeOrganizationPath(path, organization)
    if (!safePath) return

    const location: SavedOrganizationLocation = {
      organizationId: organization.id,
      path: safePath,
      timestamp: Date.now(),
    }
    sessionStorage.setItem(
      LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY,
      JSON.stringify(location),
    )
  } catch {
    // Navigation can still fall back to the landing page if storage is unavailable.
  }
}

export const getLastValidOrganizationLocation = (
  organization: OrganizationContext,
): string | null => {
  try {
    const serialized = sessionStorage.getItem(
      LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY,
    )
    if (!serialized) return null

    const location = JSON.parse(serialized) as SavedOrganizationLocation
    if (
      location.organizationId !== organization.id ||
      !Number.isFinite(location.timestamp) ||
      location.timestamp < getLastExplicitLoginAt()
    ) {
      return null
    }

    return getSafeOrganizationPath(location.path, organization)
  } catch {
    return null
  }
}
